import { useQuery } from "@tanstack/react-query";
import { useParams, Link, useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { getAppIcon, getAppColor, formatCurrency, getLicenseStatusColor, isLicenseExpired } from "@/lib/utils";
import { License, Application, Employee, Team, PaymentCard, HistoricalRenewal } from "@shared/schema";
import { ArrowLeft, Edit, Info, User, Building2, CreditCard, Calendar, Package, Users, Clock, Plus } from "lucide-react";
import { useState, useEffect } from "react";
import EditLicenseDialog from "@/components/licenses/edit-license-dialog";
import AddHistoricalRenewalDialog from "@/components/licenses/add-historical-renewal-dialog";

// Helper function to format dates properly
const formatDate = (date: string | Date | null) => {
  if (!date) return "N/A";
  try {
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString();
  } catch (err) {
    console.error("Error formatting date:", err);
    return "Invalid date";
  }
};

export default function LicenseDetail() {
  const { id } = useParams();
  const licenseId = id ? parseInt(id) : null;
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [historicalDialogOpen, setHistoricalDialogOpen] = useState(false);
  const [location] = useLocation();
  
  // Check for edit=true URL parameter to automatically open the edit dialog
  useEffect(() => {
    // Parse URL search parameters
    const params = new URLSearchParams(location.split('?')[1]);
    const shouldEdit = params.get('edit') === 'true';
    
    if (shouldEdit) {
      setEditDialogOpen(true);
    }
  }, [location]);
  
  // Add event listener for opening edit dialog
  useEffect(() => {
    const handleOpenEditDialog = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail && customEvent.detail.licenseId === licenseId) {
        setEditDialogOpen(true);
      }
    };
    
    window.addEventListener('openEditLicenseDialog', handleOpenEditDialog);
    
    return () => {
      window.removeEventListener('openEditLicenseDialog', handleOpenEditDialog);
    };
  }, [licenseId]);
  
  // Query the license data
  const { data: license, isLoading: licenseLoading } = useQuery<License>({
    queryKey: ['/api/licenses', licenseId],
    queryFn: async () => {
      if (!licenseId) throw new Error("License ID is required");
      const res = await fetch(`/api/licenses/${licenseId}`);
      if (!res.ok) {
        throw new Error('Failed to fetch license');
      }
      return res.json();
    },
    enabled: licenseId !== null,
  });

  // Query application data to display application details
  const { data: applications = [], isLoading: appsLoading } = useQuery<Application[]>({
    queryKey: ['/api/applications'],
    enabled: licenseId !== null,
  });
  
  // Query employees assigned to this license
  const { data: assignedEmployees = [], isLoading: employeesLoading } = useQuery<Employee[]>({
    queryKey: ['/api/licenses', licenseId, 'employees'],
    queryFn: async () => {
      if (!licenseId) throw new Error("License ID is required");
      try {
        const res = await fetch(`/api/licenses/${licenseId}/employees`);
        if (!res.ok) return [];
        return res.json();
      } catch (error) {
        console.error("Error fetching license employees:", error);
        return [];
      }
    },
    enabled: licenseId !== null,
  });
  
  // Query teams assigned to this license
  const { data: assignedTeams = [], isLoading: teamsLoading } = useQuery<Team[]>({
    queryKey: ['/api/licenses', licenseId, 'teams'],
    queryFn: async () => {
      if (!licenseId) throw new Error("License ID is required");
      try {
        const res = await fetch(`/api/licenses/${licenseId}/teams`);
        if (!res.ok) return [];
        return res.json();
      } catch (error) {
        console.error("Error fetching license teams:", error);
        return [];
      }
    },
    enabled: licenseId !== null,
  });
  
  // Query payment card associated with this license
  const { data: paymentCard, isLoading: cardLoading } = useQuery<PaymentCard | null>({
    queryKey: ['/api/licenses', licenseId, 'payment-card'],
    queryFn: async () => {
      if (!licenseId) throw new Error("License ID is required");
      try {
        const res = await fetch(`/api/licenses/${licenseId}/payment-card`);
        if (!res.ok) return null;
        return res.json();
      } catch (error) {
        console.error("Error fetching license payment card:", error);
        return null;
      }
    },
    enabled: licenseId !== null,
  });

  // Query historical renewals for this license
  const { data: historicalRenewals = [], isLoading: renewalsLoading } = useQuery<HistoricalRenewal[]>({
    queryKey: ['/api/licenses', licenseId, 'historical-renewals'],
    queryFn: async () => {
      if (!licenseId) throw new Error("License ID is required");
      try {
        const res = await fetch(`/api/licenses/${licenseId}/historical-renewals`);
        if (!res.ok) return [];
        return res.json();
      } catch (error) {
        console.error("Error fetching historical renewals:", error);
        return [];
      }
    },
    enabled: licenseId !== null,
  });
  
  // Keep this for backward compatibility until fully migrated
  const { data: assignedUsers = [], isLoading: usersLoading } = useQuery({
    queryKey: ['/api/licenses', licenseId, 'users'],
    queryFn: async () => {
      if (!licenseId) throw new Error("License ID is required");
      try {
        const res = await fetch(`/api/user-licenses?licenseId=${licenseId}`);
        if (!res.ok) return [];
        return res.json();
      } catch (error) {
        console.error("Error fetching license users:", error);
        return [];
      }
    },
    enabled: licenseId !== null,
  });
  
  // Find the associated application
  const application = license?.applicationId 
    ? applications.find(app => app.id === license.applicationId) 
    : undefined;
    
  // Calculate combined loading state after all query hooks have been defined
  const isLoading = licenseLoading || appsLoading || employeesLoading || teamsLoading || cardLoading || renewalsLoading || usersLoading;

  if (isLoading) {
    return (
      <div>
        <div className="mb-6">
          <Skeleton className="h-8 w-64" />
        </div>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!license) {
    return (
      <div>
        <div className="mb-6 flex items-center">
          <Button asChild variant="ghost" size="sm" className="mr-2">
            <Link to="/licenses">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Licenses
            </Link>
          </Button>
        </div>
        <Card>
          <CardContent className="p-6 text-center">
            <h3 className="text-lg font-medium">License not found</h3>
            <p className="text-sm text-muted-foreground mt-2">The license you're looking for could not be found or may have been deleted.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isLifetimeLic = license.billingCycle === 'Lifetime' || license.billingCycle === 'One-Time';
  const effectiveStatus = (!isLifetimeLic && license.status !== 'discontinued' && isLicenseExpired(license.renewalDate))
    ? 'expired'
    : (license.status || 'unknown');
  const statusColor = getLicenseStatusColor(effectiveStatus);
  const assignedPercentage = license.totalSeats > 0 
    ? Math.round((license.assignedSeats / license.totalSeats) * 100) 
    : 0;

  // Calculate days until renewal
  const renewalDate = license.renewalDate ? new Date(license.renewalDate) : null;
  const now = new Date();
  const daysUntilRenewal = renewalDate ? Math.floor((renewalDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)) : 0;

  return (
    <div>
      <EditLicenseDialog 
        licenseId={licenseId}
        isOpen={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
      />
      
      <div className="mb-6 flex items-center justify-between">
        <Button asChild variant="ghost" size="sm">
          <Link to="/licenses">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Licenses
          </Link>
        </Button>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => setEditDialogOpen(true)}
        >
          <Edit className="mr-2 h-4 w-4" />
          Edit License
        </Button>
      </div>

      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex items-center mb-6">
            <div className={`flex-shrink-0 h-16 w-16 rounded ${getAppColor((application as any)?.logo || null)} flex items-center justify-center mr-4`}>
              <i className={`${getAppIcon((application as any)?.logo || null)} text-2xl`}></i>
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-semibold mb-1">{license.name}</h3>
              <p className="text-muted-foreground">{application?.name || 'Unknown Application'} • {license.billingCycle}</p>
              <div className="flex gap-2 mt-2">
                <Badge className={statusColor}>
                  {effectiveStatus.charAt(0).toUpperCase() + effectiveStatus.slice(1)}
                </Badge>
              </div>
            </div>
          </div>
          
          <Tabs defaultValue="details" className="mt-4">
            <TabsList className="grid w-full grid-cols-2 md:grid-cols-5">
              <TabsTrigger value="details" className="flex items-center justify-center">
                <Info className="h-4 w-4 mr-2" />
                <span>Details</span>
              </TabsTrigger>
              <TabsTrigger value="renewals" className="flex items-center justify-center">
                <Clock className="h-4 w-4 mr-2" />
                <span>History</span>
              </TabsTrigger>
              <TabsTrigger value="employees" className="flex items-center justify-center">
                <User className="h-4 w-4 mr-2" />
                <span>Employees</span>
              </TabsTrigger>
              <TabsTrigger value="teams" className="flex items-center justify-center">
                <Building2 className="h-4 w-4 mr-2" />
                <span>Teams</span>
              </TabsTrigger>
              <TabsTrigger value="billing" className="flex items-center justify-center">
                <CreditCard className="h-4 w-4 mr-2" />
                <span>Billing</span>
              </TabsTrigger>
            </TabsList>
            
            {/* Details Tab */}
            <TabsContent value="details" className="space-y-4 pt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-1">Start Date</h4>
                  <p>{formatDate(license.startDate || null)}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-1">Renewal Date</h4>
                  <p className={daysUntilRenewal <= 7 ? "text-red-600 font-medium" : ""}>
                    {formatDate(license.renewalDate || null)}
                    {daysUntilRenewal <= 30 && (
                      <span className="text-xs block">
                        {daysUntilRenewal <= 0 
                          ? "Overdue" 
                          : `${daysUntilRenewal} days remaining`}
                      </span>
                    )}
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-1">License Status</h4>
                  <p>{effectiveStatus.charAt(0).toUpperCase() + effectiveStatus.slice(1)}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-1">Vendor</h4>
                  <p>{(application as any)?.vendor || "N/A"}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-1">Total Seats</h4>
                  <p>{license.totalSeats}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-1">Assigned Seats</h4>
                  <p>{license.assignedSeats} ({assignedPercentage}%)</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-1">Category</h4>
                  <p>{application?.category || "N/A"}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-1">License Type</h4>
                  <p>{"Standard"}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-1">Website</h4>
                  <p>
                    {(application as any)?.website ? (
                      <a 
                        href={(application as any).website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        {(application as any).website}
                      </a>
                    ) : (
                      "N/A"
                    )}
                  </p>
                </div>
              </div>

              <div className="mt-6">
                <h4 className="text-sm font-medium mb-2">Seat Allocation</h4>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${assignedPercentage > 90 ? 'bg-red-500' : 'bg-primary'}`}
                    style={{ width: `${assignedPercentage}%` }}
                  ></div>
                </div>
                <div className="flex justify-between mt-1 text-xs text-muted-foreground">
                  <span>Used: {license.assignedSeats}</span>
                  <span>Total: {license.totalSeats}</span>
                </div>
              </div>
            </TabsContent>
            
            {/* Renewal History Tab */}
            <TabsContent value="renewals" className="pt-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium">Renewal Timeline</h4>
                  <Button
                    size="sm"
                    onClick={() => setHistoricalDialogOpen(true)}
                    data-testid="button-add-historical-renewal"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Historical Renewal
                  </Button>
                </div>
                
                {renewalsLoading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-16 w-full" />
                    <Skeleton className="h-16 w-full" />
                  </div>
                ) : historicalRenewals.length > 0 ? (
                  <div className="space-y-3">
                    {historicalRenewals
                      .sort((a, b) => {
                        const dateA = a.renewalDate ? new Date(a.renewalDate).getTime() : 0;
                        const dateB = b.renewalDate ? new Date(b.renewalDate).getTime() : 0;
                        return dateB - dateA;
                      })
                      .map((renewal) => (
                        <div 
                          key={renewal.id} 
                          className="p-4 border rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Calendar className="h-4 w-4 text-muted-foreground" />
                                <span className="font-medium">{formatDate(renewal.renewalDate)}</span>
                                <Badge variant="outline" className="text-xs">Historical</Badge>
                              </div>
                              <div className="text-sm text-muted-foreground mt-1">
                                Cost: <span className="font-semibold text-foreground">{formatCurrency(renewal.newCost)}</span>
                                {renewal.previousCost && (
                                  <span className="ml-2">
                                    (Previous: {formatCurrency(renewal.previousCost)})
                                  </span>
                                )}
                              </div>
                              {renewal.notes && (
                                <p className="text-xs text-muted-foreground mt-2 italic">{renewal.notes}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className="text-center p-8 border rounded-lg bg-slate-50">
                    <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                    <p className="text-sm font-medium mb-1">No historical renewals</p>
                    <p className="text-xs text-muted-foreground mb-4">
                      Track past renewals and cost changes for this license
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setHistoricalDialogOpen(true)}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add First Renewal
                    </Button>
                  </div>
                )}
              </div>
            </TabsContent>
            
            {/* Employees Tab */}
            <TabsContent value="employees" className="pt-4">
              <div>
                <h4 className="text-sm font-medium mb-3">Assigned Employees</h4>
                {employeesLoading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-8 w-full" />
                    <Skeleton className="h-8 w-full" />
                  </div>
                ) : assignedEmployees.length > 0 ? (
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {assignedEmployees.map((employee) => (
                      <div key={employee.id} className="flex items-center p-3 bg-slate-50 rounded text-sm">
                        <div className="bg-slate-200 rounded-full h-8 w-8 flex items-center justify-center mr-3 flex-shrink-0">
                          {employee.fullName ? employee.fullName.charAt(0).toUpperCase() : 'E'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-base truncate">{employee.fullName}</div>
                          <div className="text-xs text-muted-foreground flex flex-col sm:flex-row sm:items-center">
                            <span className="truncate">{employee.email}</span>
                            {employee.jobTitle && (
                              <span className="bg-slate-200 rounded-full px-2 py-0.5 text-xs mt-1 sm:mt-0 sm:ml-2 inline-block sm:inline w-fit">
                                {employee.jobTitle}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center p-4 border rounded-md bg-slate-50">
                    <p className="text-sm text-muted-foreground">No employees assigned to this license.</p>
                  </div>
                )}
              </div>
            </TabsContent>
            
            {/* Teams Tab */}
            <TabsContent value="teams" className="pt-4">
              <div>
                <h4 className="text-sm font-medium mb-3">Assigned Teams</h4>
                {teamsLoading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-8 w-full" />
                    <Skeleton className="h-8 w-full" />
                  </div>
                ) : assignedTeams.length > 0 ? (
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {assignedTeams.map((team) => (
                      <div key={team.id} className="flex items-center p-3 bg-slate-50 rounded text-sm">
                        <div className="bg-slate-200 rounded h-10 w-10 flex items-center justify-center mr-3 flex-shrink-0">
                          <Building2 className="h-5 w-5 text-slate-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-base truncate">{team.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {team.description && (
                              <span className="truncate block">{team.description}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center p-4 border rounded-md bg-slate-50">
                    <p className="text-sm text-muted-foreground">No teams assigned to this license.</p>
                  </div>
                )}
              </div>
            </TabsContent>
            
            {/* Billing Tab */}
            <TabsContent value="billing" className="pt-4">
              <div className="space-y-6">
                <div>
                  <h4 className="text-sm font-medium mb-2">Cost Details</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                    <div>
                      <p className="text-xs text-muted-foreground">Cost per seat</p>
                      <p className="text-lg font-semibold">{formatCurrency(license.cost / license.totalSeats)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Total cost</p>
                      <p className="text-lg font-semibold">{formatCurrency(license.cost)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Billing cycle</p>
                      <p>{license.billingCycle}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Currency</p>
                      <p>{license.currency || "USD"}</p>
                    </div>
                  </div>
                </div>
                
                {/* Payment Card Details */}
                <div>
                  <h4 className="text-sm font-medium mb-3">Payment Method</h4>
                  {cardLoading ? (
                    <div className="space-y-2">
                      <Skeleton className="h-8 w-full" />
                      <Skeleton className="h-8 w-full" />
                    </div>
                  ) : paymentCard ? (
                    <div className="p-4 bg-slate-50 rounded-md border">
                      <div className="flex items-center">
                        <div className="bg-slate-200 rounded h-10 w-10 flex items-center justify-center mr-3 flex-shrink-0">
                          <CreditCard className="h-5 w-5 text-slate-600" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">{paymentCard.cardName}</p>
                          <div className="flex items-center text-sm text-muted-foreground">
                            <span>{paymentCard.cardType} •</span>
                            <span className="ml-1">••••{paymentCard.lastFourDigits}</span>
                            <span className="ml-2 px-2 py-0.5 bg-slate-200 rounded-full text-xs">
                              {paymentCard.cardProvider}
                            </span>
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            Expires: {paymentCard.expiryMonth}/{paymentCard.expiryYear}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center p-4 border rounded-md bg-slate-50">
                      <p className="text-sm text-muted-foreground">No payment method associated with this license.</p>
                    </div>
                  )}
                </div>
                
                {/* Annual Cost Projection */}
                <div>
                  <h4 className="text-sm font-medium mb-3">Cost Projection</h4>
                  <div className="p-4 bg-slate-50 rounded-md border">
                    <div className="space-y-3">
                      <div>
                        <p className="text-xs text-muted-foreground">Annual cost</p>
                        <p className="text-lg font-semibold">
                          {license.billingCycle === "Monthly" 
                            ? formatCurrency(license.cost * 12) 
                            : license.billingCycle === "Quarterly" 
                              ? formatCurrency(license.cost * 4)
                              : license.billingCycle === "Annually" 
                                ? formatCurrency(license.cost)
                                : formatCurrency(license.cost)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Cost per employee per month</p>
                        <p className="text-sm">
                          {license.assignedSeats > 0 
                            ? formatCurrency((license.billingCycle === "Monthly" 
                                ? license.cost 
                                : license.billingCycle === "Quarterly" 
                                  ? license.cost / 3
                                  : license.billingCycle === "Annually"
                                    ? license.cost / 12
                                    : license.cost) / license.assignedSeats)
                            : formatCurrency(0)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Add Historical Renewal Dialog */}
      {historicalDialogOpen && licenseId && (
        <AddHistoricalRenewalDialog
          licenseId={licenseId}
          licenseName={license.name}
          isOpen={historicalDialogOpen}
          onClose={() => setHistoricalDialogOpen(false)}
        />
      )}
    </div>
  );
}